import SearchableDropdown from '../common/SearchableDropdown';
import ClearableDatePicker from '../common/ClearableDatePicker';
import ClearableInput from '../common/ClearableInput';

const ContactRequestsFilters = ({
  showFilters,
  setShowFilters,
  filterInputs,
  handleInputChange,
  countries,
  profiles,
  isAdmin,
  applyFilters,
  clearAllFilters,
  parseDateString,
  formatDateForAPI,
  loading,
  countryItems,
  profileItems
}) => {
  return (
    <div className={isAdmin ? '' : 'lg:max-w-2xl lg:mx-auto'}>
      {/* Filter Toggle Button */}
      <div className={`${showFilters ? 'mb-4' : 'mb-2'} ${isAdmin ? '' : 'lg:flex lg:justify-center'}`}>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center space-x-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors ${isAdmin ? '' : 'lg:ml-[-2.5rem]'}`}
          data-intro="filter-toggle"
        >
          <svg
            className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          <span>{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
        </button>
      </div>

      {/* Filters Section */}
      <div
        className={`bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 transition-all duration-300 ease-in-out ${
          showFilters
            ? 'mb-6 max-h-screen opacity-100 p-4 overflow-visible'
            : 'max-h-0 opacity-0 p-0 overflow-hidden'
        }`}
      >
        <div className={`grid grid-cols-1 gap-4 ${isAdmin ? 'md:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-2'}`}>
          {/* User Filter - Admin Only */}
          {isAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                User (Name or Email)
              </label>
              <ClearableInput
                value={filterInputs.user}
                onChange={(value) => handleInputChange('user', value)}
                onClear={() => handleInputChange('user', '')}
                placeholder="Search by name or email..."
              />
            </div>
          )}

          {/* Client Filter - Admin Only */}
          {isAdmin && profiles.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Client
              </label>
              <SearchableDropdown
                items={profileItems}
                onSelect={(selectedItem) => handleInputChange('profile', selectedItem.value)}
                onClear={() => handleInputChange('profile', '')}
                buttonPlaceholder="All Clients"
                searchPlaceholder="Search clients..."
                widthClass="w-full"
                selectedValue={filterInputs.profile}
              />
            </div>
          )}

          {/* Country Filter */}
          {countries && countries.length > 1 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Country
              </label>
              <SearchableDropdown
                items={countryItems}
                onSelect={(selectedItem) => handleInputChange('country', selectedItem.value)}
                onClear={() => handleInputChange('country', '')}
                buttonPlaceholder="All Countries"
                widthClass="w-full"
                selectedValue={filterInputs.country}
                searchable={false}
              />
            </div>
          )}

          {/* From Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              From Date
            </label>
            <ClearableDatePicker
              selected={parseDateString(filterInputs.fromDate)}
              onChange={(date) => handleInputChange('fromDate', formatDateForAPI(date))}
              onClear={() => handleInputChange('fromDate', '')}
              placeholder="Select from date"
              maxDate={parseDateString(filterInputs.toDate)}
            />
          </div>

          {/* To Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              To Date
            </label>
            <ClearableDatePicker
              selected={parseDateString(filterInputs.toDate)}
              onChange={(date) => handleInputChange('toDate', formatDateForAPI(date))}
              onClear={() => handleInputChange('toDate', '')}
              placeholder="Select to date"
              minDate={parseDateString(filterInputs.fromDate)}
            />
          </div>

          {/* Diet Request ID Filter - Admin Only */}
          {isAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Diet Request ID
              </label>
              <ClearableInput
                value={filterInputs.recommendationId}
                onChange={(value) => handleInputChange('recommendationId', value)}
                onClear={() => handleInputChange('recommendationId', '')}
                placeholder="Filter by ID..."
              />
            </div>
          )}

          {/* Post Code Filter - Admin Only */}
          {isAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Post Code
              </label>
              <ClearableInput
                value={filterInputs.postCode}
                onChange={(value) => handleInputChange('postCode', value)}
                onClear={() => handleInputChange('postCode', '')}
                placeholder="Filter by post code..."
              />
            </div>
          )}

          {/* Phone Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Phone
            </label>
            <ClearableInput
              value={filterInputs.phone}
              onChange={(value) => handleInputChange('phone', value)}
              onClear={() => handleInputChange('phone', '')}
              placeholder="Filter by phone..."
            />
          </div>

          {/* Comments Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Comments
            </label>
            <ClearableInput
              value={filterInputs.comments}
              onChange={(value) => handleInputChange('comments', value)}
              onClear={() => handleInputChange('comments', '')}
              placeholder="Filter by comments..."
            />
          </div>
        </div>

        {/* Filter Action Buttons */}
        <div className="mt-4 flex justify-center space-x-3">
          <button
            onClick={applyFilters}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <span>Filter</span>
          </button>
          <button
            onClick={clearAllFilters}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:text-white dark:focus:ring-gray-700"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            <span>Clear</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactRequestsFilters;
